// C.1 — батч-1: rare/epic/legendary юниты Глобала из v6 xlsx.
//
// Включает: Резонанс (legendary extender), Феникс/Нарвал (legendary aura),
// Кулак/Рокировка (epic aura), Капитан G-Sec / Старшина «Зубр» (rare ally_summoned),
// Командир Аккорд (epic suppression aura), Симфония / Координатор Эхо (rare/epic
// ally_healed / enemy_died), Лейтенант Сдвиг / Тактик Ёж (epic/rare any_unit_moved),
// Стратег Гамбит (legendary EOT), Учебка G-Sec (rare turn_end summon),
// Знамя Бастион (rare aura by id), Гвардия Сектор-А (vanilla taunt),
// Сапёр-подавитель Кладовщик (rare order armor_break), Глушилка GS-9 (rare order silence),
// Корректировщица Ника (rare on-attack +1 vs suppressed/silenced),
// Авангард-боец (rare turn_end heal_to_full если есть Глобал-хилер),
// БПЛА Стервятник-4 / Разведчик Шёпот (rare on_play линия-зависимые баффы),
// Дрон-курьер (rare order neighbors), Бастион Полковник (epic on_self_damage heal_ally),
// Координатор Хронос (epic order 6 split damage line_2), Полевой медик «Янтарь»
// (common turn_end heal priority — уже в common-units?), Хирург G-Sec (rare heal 2 priority).

import { registerImplementedAbility } from '../../warn-unimplemented.js';
import { applyPermanentBuff, applyTemporaryBuff, removePermanentBuffsBySource } from '../../../buffs.js';
import { healUnit, registerOnAllyHealedObserver } from '../../livlife/heal.js';
import { applyDamage } from '../../../combat.js';
import { applySilence, applySilenceWithTTL } from '../../../keywords/silence.js';
import { resolveTarget } from '../../../effects/targets.js';
import { getHealTarget } from '../../../heal-priority.js';
import { registerOnEndOfTurnTrigger } from '../../livlife/eot-triggers.js';
import { registerOnStartOfTurnTrigger } from '../../livlife/start-of-turn-triggers.js';
import { registerOnAttackTrigger } from '../../livlife/on-attack-triggers.js';
import { registerOnDamageTakenTrigger } from '../../livlife/on-damage-triggers.js';
import {
  registerOnUnitDiedExtraHandler,
  registerUnitSummonedExtraHandler,
  registerUnitLeftFieldExtraHandler
} from '../../deus/hooks.js';
import { summonUnit } from '../summon.js';
import { recomputeReinforcement } from '../../../keywords/reinforcement.js';
import { recomputeSuppression } from '../../../keywords/suppression.js';
import { registerC1Order } from './order-registry.js';

const GSEC_FIGHTER_ID = 'global_gsec_fighter';

// ============================================================
// === Резонанс (legendary) — расширитель Подавления          ===
// ============================================================
const REZONANS_ID = 'global_dirizhor2_rezonans';

function recomputeRezonansExtender(state) {
  if (!state?.players) return;
  if (!state.suppressionExtenderActive) state.suppressionExtenderActive = [false, false];
  for (let i = 0; i < state.players.length; i++) {
    const p = state.players[i];
    let active = false;
    if (p?.field) {
      for (const row of p.field) for (const u of row || []) {
        if (u && u.id === REZONANS_ID && u.isAlive && u.isAlive() && !u.current?.silenced) {
          active = true; break;
        }
      }
    }
    state.suppressionExtenderActive[i] = active;
  }
  recomputeSuppression(state);
}
registerUnitSummonedExtraHandler((state) => recomputeRezonansExtender(state));
registerOnUnitDiedExtraHandler((state) => recomputeRezonansExtender(state));
registerUnitLeftFieldExtraHandler((state) => recomputeRezonansExtender(state));
registerImplementedAbility(REZONANS_ID, 'aura');

// ============================================================
// === Командир «Феникс» (legendary) — EOT AoE heal+dmg       ===
// ============================================================
// dsl_trigger1: turn_end, composite — heal 2 ally_aoe_all + damage 1 enemy_aoe_all.
const FENIKS_ID = 'global_komandir_feniks';
registerOnEndOfTurnTrigger(FENIKS_ID, (state, source) => {
  if (!source || source.id !== FENIKS_ID) return;
  if (source.current?.silenced) return;
  const owner = state.players?.[source.ownerId];
  const enemy = state.players?.[1 - source.ownerId];
  if (owner?.field) {
    for (const row of owner.field) for (const u of row || []) {
      if (u && u.isAlive && u.isAlive()) healUnit(state, u, 2, source);
    }
  }
  if (enemy?.field) {
    // Снимок целей перед применением урона (юниты могут умереть).
    const targets = [];
    for (const row of enemy.field) for (const u of row || []) {
      if (u && u.isAlive && u.isAlive()) targets.push(u);
    }
    for (const t of targets) applyDamage(t, 1, { state, source });
  }
});
registerImplementedAbility(FENIKS_ID, 'trigger');

// ============================================================
// === Командир «Нарвал» (legendary) — aura G-Sec +1/0/2     ===
// ============================================================
// Аура: при призыве союзного Бойца G-Sec он получает +1/0/2.
// Реализация через unit-summoned hook.
const NARVAL_ID = 'global_komandir_narval';
registerUnitSummonedExtraHandler((state, ownerIdx, summonedCard) => {
  if (!summonedCard || summonedCard.id !== GSEC_FIGHTER_ID) return;
  const owner = state.players?.[ownerIdx];
  if (!owner?.field) return;
  for (const row of owner.field) for (const u of row || []) {
    if (!u || u.id !== NARVAL_ID) continue;
    if (u.current?.silenced) continue;
    if (!u.isAlive || !u.isAlive()) continue;
    applyPermanentBuff(state, summonedCard, { atk: 1, armor: 0, hp: 2 }, NARVAL_ID, 'Нарвал');
    return; // одна Аура — один бафф, лимит=1 в колоде.
  }
});
registerImplementedAbility(NARVAL_ID, 'aura');

// ============================================================
// === Полевой штаб «Кулак» (epic) — модификация Подкрепления ===
// ============================================================
// Аура: Подкрепление становится +1 за КАЖДОГО (вместо +1 за каждых двух).
// Реализуем через флаг state.reinforcementPerOneActive[ownerIdx]=true и
// модификацию формулы в reinforcement.js.
const KULAK_ID = 'global_polevoy_shtab_kulak';

function recomputeKulakFlag(state) {
  if (!state?.players) return;
  if (!state.reinforcementPerOneActive) state.reinforcementPerOneActive = [false, false];
  for (let i = 0; i < state.players.length; i++) {
    const p = state.players[i];
    let active = false;
    if (p?.field) {
      for (const row of p.field) for (const u of row || []) {
        if (u && u.id === KULAK_ID && u.isAlive && u.isAlive() && !u.current?.silenced) {
          active = true; break;
        }
      }
    }
    state.reinforcementPerOneActive[i] = active;
  }
  // Пересчёт Подкрепления чтобы применить новую формулу.
  recomputeReinforcement(state);
}
registerUnitSummonedExtraHandler((state) => recomputeKulakFlag(state));
registerOnUnitDiedExtraHandler((state) => recomputeKulakFlag(state));
registerUnitLeftFieldExtraHandler((state) => recomputeKulakFlag(state));
registerImplementedAbility(KULAK_ID, 'aura');

// ============================================================
// === Полевой штаб «Рокировка» (epic) — aura 2-я линия +1/0/0 ===
// ============================================================
const ROKIROVKA_ID = 'global_polevoy_shtab_rokirovka';
const ROKIROVKA_SOURCE = `${ROKIROVKA_ID}:aura`;

function recomputeRokirovka(state) {
  if (!state?.players) return;
  for (let pIdx = 0; pIdx < state.players.length; pIdx++) {
    const p = state.players[pIdx];
    if (!p?.field) continue;
    for (const row of p.field) for (const u of row || []) {
      if (!u) continue;
      removePermanentBuffsBySource(state, u, (id) => typeof id === 'string' && id.startsWith(ROKIROVKA_SOURCE));
    }
    // Активна ли Рокировка у этого игрока?
    let active = false;
    for (const row of p.field) for (const u of row || []) {
      if (u && u.id === ROKIROVKA_ID && u.isAlive && u.isAlive() && !u.current?.silenced) {
        active = true; break;
      }
    }
    if (!active) continue;
    // +1/0/0 всем союзникам на 2-й линии.
    const row2 = p.field[1] || [];
    for (const u of row2) {
      if (!u || !u.isAlive || !u.isAlive()) continue;
      applyPermanentBuff(state, u, { atk: 1, armor: 0, hp: 0 }, ROKIROVKA_SOURCE, 'Рокировка');
    }
  }
}
registerUnitSummonedExtraHandler((state) => recomputeRokirovka(state));
registerOnUnitDiedExtraHandler((state) => recomputeRokirovka(state));
registerUnitLeftFieldExtraHandler((state) => recomputeRokirovka(state));
registerImplementedAbility(ROKIROVKA_ID, 'aura');

// ============================================================
// === Знамя «Бастион» (rare) — aura G-Sec +1/0/0             ===
// ============================================================
const ZNAMYA_ID = 'global_znamya_bastion';
const ZNAMYA_SOURCE = `${ZNAMYA_ID}:aura`;

function recomputeZnamya(state) {
  if (!state?.players) return;
  for (let pIdx = 0; pIdx < state.players.length; pIdx++) {
    const p = state.players[pIdx];
    if (!p?.field) continue;
    for (const row of p.field) for (const u of row || []) {
      if (!u) continue;
      removePermanentBuffsBySource(state, u, (id) => typeof id === 'string' && id.startsWith(ZNAMYA_SOURCE));
    }
    let active = false;
    for (const row of p.field) for (const u of row || []) {
      if (u && u.id === ZNAMYA_ID && u.isAlive && u.isAlive() && !u.current?.silenced) {
        active = true; break;
      }
    }
    if (!active) continue;
    for (const row of p.field) for (const u of row || []) {
      if (!u || u.id !== GSEC_FIGHTER_ID) continue;
      if (!u.isAlive || !u.isAlive()) continue;
      applyPermanentBuff(state, u, { atk: 1, armor: 0, hp: 0 }, ZNAMYA_SOURCE, 'Знамя Бастион');
    }
  }
}
registerUnitSummonedExtraHandler((state) => recomputeZnamya(state));
registerOnUnitDiedExtraHandler((state) => recomputeZnamya(state));
registerUnitLeftFieldExtraHandler((state) => recomputeZnamya(state));
registerImplementedAbility(ZNAMYA_ID, 'aura');

// ============================================================
// === Старший медик «Кардинал» (epic) — EOT regen line 1     ===
// ============================================================
// Аура: все союзники на 1-й линии +1 hp в конце своего хода (постоянная регенерация).
// Реализация: registerOnEndOfTurnTrigger по id Кардинала, эффект — heal 1 каждому
// на 1-й линии.
const KARDINAL_ID = 'global_starshiy_medik_kardinal';
registerOnEndOfTurnTrigger(KARDINAL_ID, (state, source) => {
  if (!source || source.id !== KARDINAL_ID) return;
  if (source.current?.silenced) return;
  const owner = state.players?.[source.ownerId];
  const row = owner?.field?.[0] || [];
  for (const u of row) {
    if (!u || !u.isAlive || !u.isAlive()) continue;
    healUnit(state, u, 1, source);
  }
});
registerImplementedAbility(KARDINAL_ID, 'aura');

// ============================================================
// === Капитан G-Sec (rare) — triggered self-buff             ===
// === Старшина «Зубр» (rare) — triggered self-buff           ===
// === Сержант «Гриф» (epic) — composite на ally G-Sec        ===
// ============================================================
const KAPITAN_GSEC_ID = 'global_kapitan_gsec';
const ZUBR_ID = 'global_starshina_zubr';
const GRIF_ID = 'global_serzhant_grif';

registerUnitSummonedExtraHandler((state, ownerIdx, summonedCard) => {
  if (!summonedCard) return;
  const owner = state.players?.[ownerIdx];
  if (!owner?.field) return;

  for (const row of owner.field) for (const u of row || []) {
    if (!u) continue;
    if (!u.isAlive || !u.isAlive()) continue;
    if (u.current?.silenced) continue;
    if (u === summonedCard) continue;

    // Капитан G-Sec: same line + любой Глобал-юнит, лимит +3.
    if (u.id === KAPITAN_GSEC_ID
      && summonedCard.faction === 'global'
      && summonedCard.line === u.line) {
      const cnt = u.kapitanStacks || 0;
      if (cnt < 3) {
        applyPermanentBuff(state, u, { atk: 1, armor: 0, hp: 0 }, KAPITAN_GSEC_ID, 'Капитан G-Sec');
        u.kapitanStacks = cnt + 1;
      }
    }
    // Старшина Зубр: only on G-Sec fighter, +1/0/1 stack <3.
    if (u.id === ZUBR_ID && summonedCard.id === GSEC_FIGHTER_ID) {
      const cnt = u.zubrStacks || 0;
      if (cnt < 3) {
        applyPermanentBuff(state, u, { atk: 1, armor: 0, hp: 1 }, ZUBR_ID, 'Старшина Зубр');
        u.zubrStacks = cnt + 1;
      }
    }
    // Сержант Гриф: only on G-Sec — composite (буфф цели +1/0/1 + self +1/0/0).
    if (u.id === GRIF_ID && summonedCard.id === GSEC_FIGHTER_ID) {
      applyPermanentBuff(state, summonedCard, { atk: 1, armor: 0, hp: 1 }, GRIF_ID, 'Гриф (G-Sec бонус)');
      applyPermanentBuff(state, u, { atk: 1, armor: 0, hp: 0 }, GRIF_ID, 'Гриф (self)');
    }
  }
});
registerImplementedAbility(KAPITAN_GSEC_ID, 'trigger');
registerImplementedAbility(ZUBR_ID, 'trigger');
registerImplementedAbility(GRIF_ID, 'trigger');

// ============================================================
// === Командир «Аккорд» (epic) — aura suppression +1 dmg     ===
// ============================================================
// Аура: все союзные Глобал-юниты с keyword Подавление наносят +1 урона.
// Реализация: при on_attack — если attacker имеет suppression и его сторона
// имеет активного Аккорда — после applyDamage добавляем +1 урона цели
// (не пирс). Так как fireOnAttackTrigger зовётся ПОСЛЕ applyDamage,
// триггер `extra_damage_on_attack` практично: повторно applyDamage(1)
// к цели, если она ещё жива.
const AKKORD_ID = 'global_komandir_ognya_akkord';

function akkordActiveForOwner(state, ownerIdx) {
  const owner = state.players?.[ownerIdx];
  if (!owner?.field) return false;
  for (const row of owner.field) for (const u of row || []) {
    if (u && u.id === AKKORD_ID && u.isAlive && u.isAlive() && !u.current?.silenced) return true;
  }
  return false;
}

// Регистрируем on_attack по cardId: фактически нам нужен любой attacker
// с keyword suppression. Реестр по cardId — поэтому регистрируем per-cardId
// для каждого Подавляющего юнита Глобала, а также для Аккорда самого.
const SUPPRESSION_GLOBAL_IDS = [
  'global_glushilka_gs9',
  'global_pulemyot_gonchaya',
  'global_razvedchik_shyopot',
  'global_saper_podavitel_kladovshchik',
  'global_snayper_lyutyi',
  'global_snayperskiy_raschet_tor',
  'global_taktik_yozh',
  'global_korrektirovshchitsa_nika',
  'global_komandir_ognya_akkord',
  'global_dirizhor2_rezonans'
];

function akkordExtraDamageHandler(state, attacker, target, dealt) {
  if (!state?.players) return;
  if (!attacker) return;
  if (!target) return;
  if (!attacker.keywords || !attacker.keywords.includes('suppression')) return;
  if (!akkordActiveForOwner(state, attacker.ownerId)) return;
  // Если уже мёртв — пропуск.
  if (target.type === 'unit' && (!target.isAlive || !target.isAlive())) return;
  if (target.type === 'hero' && (target.hp == null || target.hp <= 0)) return;
  applyDamage(target, 1, { state, source: { id: AKKORD_ID, name_ru: 'Аккорд: +1 vs Подавление' } });
}

// Корректировщица «Ника» — +1 урона по suppressed/silenced.
const NIKA_ID = 'global_korrektirovshchitsa_nika';
function nikaExtraDamageHandler(state, attacker, target) {
  if (!state?.players) return;
  if (!attacker || attacker.id !== NIKA_ID) return;
  if (!target) return;
  // Цель suppressed (есть -1 atk debuff с source suppression) или silenced.
  const isSilenced = !!target.current?.silenced;
  // suppressed — наличие отрицательной brand permBuff с suppression prefix
  let isSuppressed = false;
  if (Array.isArray(target.permanentBuffs)) {
    for (const b of target.permanentBuffs) {
      if (typeof b.id === 'string' && b.id.startsWith('suppression:')) {
        isSuppressed = true; break;
      }
    }
  }
  if (!isSilenced && !isSuppressed) return;
  if (target.type === 'unit' && (!target.isAlive || !target.isAlive())) return;
  if (target.type === 'hero' && (target.hp == null || target.hp <= 0)) return;
  applyDamage(target, 1, { state, source: { id: NIKA_ID, name_ru: 'Ника: +1 vs ослабл.' } });
}

// Подписка on_attack для всех Подавляющих + для Ники (она вкл. в список).
for (const id of SUPPRESSION_GLOBAL_IDS) {
  registerOnAttackTrigger(id, (state, attacker, target, dealt) => {
    // Сначала Аккорд (для всех Подавляющих).
    akkordExtraDamageHandler(state, attacker, target, dealt);
    // Если это Ника — также её собственный +1 по suppressed/silenced.
    if (id === NIKA_ID) {
      // target всё ещё жив? проверка внутри.
      nikaExtraDamageHandler(state, attacker, target);
    }
  });
  registerImplementedAbility(id, 'trigger');
}
// Сам Аккорд — это aura-носитель.
registerImplementedAbility(AKKORD_ID, 'aura');

// ============================================================
// === Координатор «Эхо» (rare) — ally_healed → +0/0/1 self  ===
// ============================================================
const EHO_ID = 'global_koordinator_eho';
registerOnAllyHealedObserver(EHO_ID, (state, observer, healEvent) => {
  if (!observer || observer.id !== EHO_ID) return;
  if (observer.current?.silenced) return;
  if ((healEvent?.amount || 0) <= 0) return;
  applyPermanentBuff(state, observer, { atk: 0, armor: 0, hp: 1 }, EHO_ID, 'Эхо');
});
registerImplementedAbility(EHO_ID, 'trigger');

// ============================================================
// === Координатор «Симфония» (epic) — enemy_died suppressed  ===
// ============================================================
// Триггер: когда подавленный/молчащий вражеский юнит умирает — Симфония +0/1/2.
const SIMFONIYA_ID = 'global_koordinator_simfoniya';
registerOnUnitDiedExtraHandler((state, payload) => {
  const dead = payload?.card;
  if (!dead) return;
  // died_while_suppressed_or_silenced.
  let wasSuppressed = false;
  if (Array.isArray(dead.permanentBuffs)) {
    for (const b of dead.permanentBuffs) {
      if (typeof b.id === 'string' && b.id.startsWith('suppression:')) {
        wasSuppressed = true; break;
      }
    }
  }
  const wasSilenced = !!dead.current?.silenced
    || (dead.silenceSources && dead.silenceSources.size > 0);
  if (!wasSuppressed && !wasSilenced) return;
  // Симфония — на ПРОТИВОПОЛОЖНОЙ стороне от мёртвого.
  const ownerIdx = 1 - payload.ownerIdx;
  const owner = state.players?.[ownerIdx];
  if (!owner?.field) return;
  for (const row of owner.field) for (const u of row || []) {
    if (!u || u.id !== SIMFONIYA_ID) continue;
    if (u.current?.silenced) continue;
    if (!u.isAlive || !u.isAlive()) continue;
    applyPermanentBuff(state, u, { atk: 0, armor: 1, hp: 2 }, SIMFONIYA_ID, 'Симфония');
  }
});
registerImplementedAbility(SIMFONIYA_ID, 'trigger');

// ============================================================
// === Лейтенант «Сдвиг» (epic) — any_unit_moved → +1/0/0     ===
// === Тактик «Ёж» (rare) — any_unit_moved → +1/0/0 EOT lim3   ===
// ============================================================
// Этап 23: полноценная подписка на move-events (Сдвиг, Ёж).
const SDVIG_ID = 'global_leytenant_sdvig';
const YOZH_ID = 'global_taktik_yozh';

import { registerMoveListener } from '../../../move-unit.js';
registerMoveListener((state) => {
  if (!state?.players) return;
  for (let pIdx = 0; pIdx < state.players.length; pIdx++) {
    const p = state.players[pIdx];
    if (!p?.field) continue;
    for (const row of p.field) for (const u of row || []) {
      if (!u || !u.isAlive || !u.isAlive()) continue;
      if (u.current?.silenced) continue;
      if (u.id === SDVIG_ID) {
        // +1 atk до конца игры (без лимита).
        applyPermanentBuff(state, u, { atk: 1, armor: 0, hp: 0 }, SDVIG_ID, 'Сдвиг');
      }
      if (u.id === YOZH_ID) {
        // +1 atk до конца хода, лимит +3 за матч. Используем temporary buff.
        const cnt = u.yozhStacksMatch || 0;
        if (cnt < 3) {
          const expiresAtTurn = (state.currentTurn ?? 1) + 1;
          applyTemporaryBuff(state, u, { atk: 1, armor: 0, hp: 0 }, expiresAtTurn, YOZH_ID, 'Ёж');
          u.yozhStacksMatch = cnt + 1;
        }
      }
    }
  }
});
registerImplementedAbility(SDVIG_ID, 'trigger');
registerImplementedAbility(YOZH_ID, 'trigger');

// ============================================================
// === Стратег «Гамбит» (legendary) — EOT line 1 ≥2 → 2 dmg   ===
// ============================================================
const GAMBIT_ID = 'global_strateg_gambit';
registerOnEndOfTurnTrigger(GAMBIT_ID, (state, source) => {
  if (!source || source.id !== GAMBIT_ID) return;
  if (source.current?.silenced) return;
  const enemy = state.players?.[1 - source.ownerId];
  if (!enemy?.field) return;
  const row1 = enemy.field[0] || [];
  const live = row1.filter(u => u && u.isAlive && u.isAlive());
  if (live.length < 2) return;
  for (const u of live) {
    applyDamage(u, 2, { state, source });
  }
});
registerImplementedAbility(GAMBIT_ID, 'trigger');

// ============================================================
// === Учебка G-Sec (rare) — turn_end summon G-Sec            ===
// ============================================================
const UCHEBKA_ID = 'global_uchebka_gsec';
registerOnEndOfTurnTrigger(UCHEBKA_ID, (state, source) => {
  if (!source || source.id !== UCHEBKA_ID) return;
  if (source.current?.silenced) return;
  const ownerIdx = source.ownerId;
  const owner = state.players?.[ownerIdx];
  if (!owner?.field) return;
  for (let line = 0; line < 2; line++) {
    const row = owner.field[line];
    for (let s = 0; s < row.length; s++) {
      if (row[s] === null) {
        summonUnit(state, ownerIdx, GSEC_FIGHTER_ID, line, s);
        return;
      }
    }
  }
});
registerImplementedAbility(UCHEBKA_ID, 'trigger');

// ============================================================
// === Гвардия Сектор-А (vanilla taunt)                       ===
// ============================================================
registerImplementedAbility('global_gvardia_sektor_a', 'passive');

// ============================================================
// === Сапёр-подавитель «Кладовщик» (rare) — order armor_break ===
// ============================================================
const KLADOVSHCHIK_ID = 'global_saper_podavitel_kladovshchik';
export function kladovshchikOrder(state, source) {
  if (!source) return { ok: false };
  const enemy = state.players?.[1 - source.ownerId];
  if (!enemy?.field) return { ok: false };
  const line = source.line, slot = source.slot;
  if (line == null || slot == null) return { ok: false };
  const target = enemy.field[line]?.[slot];
  if (!target) return { ok: false, reason: 'no_mirror' };
  // -2 max_armor с синхронным -2 current.
  if (target.persistentMaxArmor != null) {
    target.persistentMaxArmor = Math.max(0, target.persistentMaxArmor - 2);
  } else {
    target.persistentMaxArmor = 0;
  }
  if (target.persistentArmor != null) {
    target.persistentArmor = Math.max(0, target.persistentArmor - 2);
  } else {
    target.persistentArmor = 0;
  }
  if (target.current) {
    target.current.armor = Math.max(0, (target.current.armor || 0) - 2);
  }
  if (state.log) {
    const ownerName = state.players?.[target.ownerId]?.name || '?';
    state.log.add(`💥 Кладовщик: ${target.name_ru} [${ownerName}] -2 брони`);
  }
  return { ok: true };
}
registerImplementedAbility(KLADOVSHCHIK_ID, 'order');
registerC1Order(KLADOVSHCHIK_ID, kladovshchikOrder);

// ============================================================
// === Глушилка GS-9 (rare) — order silence random enemy на 1 ход ===
// ============================================================
const GLUSHILKA_ID = 'global_glushilka_gs9';
export function glushilkaOrder(state, source) {
  if (!source) return { ok: false };
  const enemy = state.players?.[1 - source.ownerId];
  if (!enemy?.field) return { ok: false };
  const enemies = [];
  for (const row of enemy.field) for (const u of row || []) {
    if (u && u.isAlive && u.isAlive()) enemies.push(u);
  }
  if (enemies.length === 0) return { ok: false, reason: 'no_enemy' };
  const idx = state.rng?.int ? state.rng.int(enemies.length) : Math.floor(Math.random() * enemies.length);
  const t = enemies[idx];
  // Этап 27: TTL 1 ход.
  applySilenceWithTTL(state, t, `${GLUSHILKA_ID}:${t.instanceId}`, 1);
  return { ok: true };
}
// (Глушилка уже зарегистрирована выше как 'trigger' в SUPPRESSION_GLOBAL_IDS;
// добавим и 'order' для покрытия описания.)
registerImplementedAbility(GLUSHILKA_ID, 'order');
registerC1Order(GLUSHILKA_ID, glushilkaOrder);

// ============================================================
// === Авангард-боец (rare) — turn_end heal_to_full if healer ===
// ============================================================
// Триггер: если ниже max hp и на поле есть Глобал-хилер — heal_to_full.
const AVANGARD_BOETS_ID = 'global_avangard_boets';
const GLOBAL_HEALER_IDS = new Set([
  'global_iv',                       // Майор Ив (Hero)
  'global_medik_yantar',             // Медик «Янтарь»
  'global_hirurg_gsec',              // Хирург G-Sec
  'global_starshiy_medik_kardinal',  // Старший медик «Кардинал»
  'global_komandir_feniks'           // Командир «Феникс»
]);
registerOnEndOfTurnTrigger(AVANGARD_BOETS_ID, (state, source) => {
  if (!source || source.id !== AVANGARD_BOETS_ID) return;
  if (source.current?.silenced) return;
  const cur = source.current?.hp ?? 0;
  const max = source.base?.hp ?? cur;
  if (cur >= max) return;
  const owner = state.players?.[source.ownerId];
  let hasHealer = (owner?.hero && GLOBAL_HEALER_IDS.has(owner.hero.id) && owner.hero.hp > 0);
  if (!hasHealer && owner?.field) {
    for (const row of owner.field) for (const u of row || []) {
      if (u && GLOBAL_HEALER_IDS.has(u.id) && u.isAlive && u.isAlive()) {
        hasHealer = true; break;
      }
    }
  }
  if (!hasHealer) return;
  healUnit(state, source, max - cur, source);
});
registerImplementedAbility(AVANGARD_BOETS_ID, 'trigger');

// ============================================================
// === БПЛА Стервятник-4 (rare) — on_play line 2 → +1/0/2     ===
// ============================================================
// on_play triggers — сейчас через registerUnitSummonedExtraHandler с
// проверкой self===card.
const STERVYATNIK_ID = 'global_bpla_stervyatnik4';

// ============================================================
// === Разведчик «Шёпот» (rare) — on_play line-зависимый бафф ===
// ============================================================
const SHEPOT_ID = 'global_razvedchik_shyopot';

registerUnitSummonedExtraHandler((state, ownerIdx, card) => {
  if (!card) return;
  if (card.id === STERVYATNIK_ID && card.line === 1) {
    applyPermanentBuff(state, card, { atk: 1, armor: 0, hp: 2 }, STERVYATNIK_ID, 'Стервятник-4 на 2-й');
  }
  if (card.id === SHEPOT_ID) {
    if (card.line === 0) {
      applyPermanentBuff(state, card, { atk: 2, armor: 1, hp: 0 }, SHEPOT_ID, 'Шёпот на 1-й');
    } else if (card.line === 1) {
      applyPermanentBuff(state, card, { atk: 0, armor: 0, hp: 2 }, SHEPOT_ID, 'Шёпот на 2-й');
    }
  }
});
registerImplementedAbility(STERVYATNIK_ID, 'trigger');
registerImplementedAbility(SHEPOT_ID, 'trigger');

// ============================================================
// === Дрон-курьер (rare) — order: соседи +1/1/1              ===
// ============================================================
const DRON_KURYER_ID = 'global_dron_kuryer';
export function dronKuryerOrder(state, source) {
  if (!source) return { ok: false };
  const owner = state.players?.[source.ownerId];
  if (!owner?.field) return { ok: false };
  const line = source.line, slot = source.slot;
  if (line == null || slot == null) return { ok: false };
  const row = owner.field[line] || [];
  let buffed = 0;
  for (const ofs of [-1, 1]) {
    const t = row[slot + ofs];
    if (!t || !t.isAlive || !t.isAlive()) continue;
    applyPermanentBuff(state, t, { atk: 1, armor: 1, hp: 1 }, DRON_KURYER_ID, 'Дрон-курьер');
    buffed++;
  }
  return { ok: true, buffed };
}
registerImplementedAbility(DRON_KURYER_ID, 'order');
registerC1Order(DRON_KURYER_ID, dronKuryerOrder);

// ============================================================
// === Полковник «Бастион» (epic) — on_self_damage → heal 1 ally ===
// ============================================================
const BASTION_POLK_ID = 'global_polkovnik_bastion';
registerOnDamageTakenTrigger(BASTION_POLK_ID, (state, target, damageToHp) => {
  if (!state?.players) return;
  if (damageToHp <= 0) return;
  if (target.current?.silenced) return;
  const owner = state.players[target.ownerId];
  if (!owner?.field) return;
  const allies = [];
  for (const row of owner.field) for (const u of row || []) {
    if (u && u.isAlive && u.isAlive()) allies.push(u);
  }
  if (allies.length === 0) return;
  const idx = state.rng?.int ? state.rng.int(allies.length) : Math.floor(Math.random() * allies.length);
  // Аудит-фикс под v6: «+0/1/0» — это БРОНЯ (а не лечение hp).
  applyPermanentBuff(state, allies[idx], { atk: 0, armor: 1, hp: 0 }, BASTION_POLK_ID, 'Полковник «Бастион»');
});
registerImplementedAbility(BASTION_POLK_ID, 'trigger');

// ============================================================
// === Координатор «Хронос» (epic) — order: 6 split dmg line 2 ===
// ============================================================
const HRONOS_ID = 'global_koordinator_hronos';
export function hronosOrder(state, source) {
  if (!source) return { ok: false };
  const enemy = state.players?.[1 - source.ownerId];
  if (!enemy?.field) return { ok: false };
  const targets = [];
  const row2 = enemy.field[1] || [];
  for (const u of row2) if (u && u.isAlive && u.isAlive()) targets.push(u);
  if (targets.length === 0) return { ok: true, hit: 0 };
  let hits = 0;
  for (let i = 0; i < 6; i++) {
    const live = targets.filter(u => u.isAlive && u.isAlive());
    if (live.length === 0) break;
    const idx = state.rng?.int ? state.rng.int(live.length) : Math.floor(Math.random() * live.length);
    applyDamage(live[idx], 1, { state, source });
    hits++;
  }
  return { ok: true, hits };
}
registerImplementedAbility(HRONOS_ID, 'order');
registerC1Order(HRONOS_ID, hronosOrder);

// ============================================================
// === Хирург G-Sec (rare) — turn_end heal 2 by priority      ===
// === Медик «Янтарь» (common) — turn_end heal 1 by priority  ===
// ============================================================
// Янтарь уже реализован в common-units.js. Здесь добавим Хирурга G-Sec.
const HIRURG_GSEC_ID = 'global_hirurg_gsec';
registerOnEndOfTurnTrigger(HIRURG_GSEC_ID, (state, source) => {
  if (!source || source.id !== HIRURG_GSEC_ID) return;
  if (source.current?.silenced) return;
  const t = getHealTarget(state, source);
  if (t) healUnit(state, t, 2, source);
});
registerImplementedAbility(HIRURG_GSEC_ID, 'trigger');

// ============================================================
// === Vanilla карты Глобала — регистрация чтобы убрать warn ===
// ============================================================
registerImplementedAbility('global_bastion_g12', 'passive');
registerImplementedAbility('global_dron_bargest1', 'passive');
registerImplementedAbility('global_gsec_fighter', 'passive');
registerImplementedAbility('global_strelok_kobra', 'passive');
registerImplementedAbility('global_pulemyot_gonchaya', 'passive');
registerImplementedAbility('global_snayper_lyutyi', 'passive');
registerImplementedAbility('global_snayperskiy_raschet_tor', 'passive');
